import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { AutoSizer, List } from 'react-virtualized';
import { useFormikContext } from 'formik';
import useStyles from './FindingList.styles';
import FindingItem from './item/FindingItem';
import { FindingShape } from '../../models/Finding';
import FindingCharts from '../charts/FindingCharts';
import FindingFilters from '../filters/FindingFilters';
import Can from '../../../common/contexts/abilityContext';
import { ContainerLoader } from '../../../common/components/loaders';
import { ClientCategoryShape } from '../../../client/models/ClientCategory';

const FindingList = ({ findings = [], clientCategories, patrolId, onFindingUpdate, isFetchingFindings }) => {
  const classes = useStyles();

  const { values } = useFormikContext();

  const handleFindingUpdate = useCallback((updatedFinding) => onFindingUpdate(updatedFinding, values), [onFindingUpdate, values]);

  const rowRenderer = ({ key, index, style, isVisible }) => (
    <FindingItem
      key={key}
      patrolId={patrolId}
      finding={findings[index]}
      isVisible={isVisible}
      style={style}
      onFindingUpdate={handleFindingUpdate}
      clientCategories={clientCategories}
    />
  );

  if (isFetchingFindings) return <ContainerLoader />;
  return (
    <>
      <div className={classes.header}>
        <div className={classes.title}>{`${i18n.t('patrol.finding.findings')} (${findings.length})`}</div>
        <Can I="accessAll" a="finding" passThrough>
          {(accessAll) => <FindingFilters showOnlyClientFilters={!accessAll} clientCategories={clientCategories} />}
        </Can>
      </div>

      {!findings || (findings.length === 0 && <div>{i18n.t('common.userInterface.noDataToDisplay')}</div>)}
      {findings && findings.length > 0 && (
        <>
          <Can I="accessAll" a="finding" passThrough>
            {(accessAll) => (
              <FindingCharts
                onlyShowClientLabels={!accessAll}
                findings={findings}
                isFetchingFindings={isFetchingFindings}
                clientCategories={clientCategories}
              />
            )}
          </Can>
          {!isFetchingFindings && (
            <div className={classes.list}>
              <AutoSizer>
                {({ height, width }) => <List rowRenderer={rowRenderer} rowHeight={240} width={width} height={height} rowCount={findings.length} />}
              </AutoSizer>
            </div>
          )}
        </>
      )}
    </>
  );
};

FindingList.propTypes = {
  findings: PropTypes.arrayOf(FindingShape).isRequired,
  clientCategories: PropTypes.arrayOf(ClientCategoryShape).isRequired,
  isFetchingFindings: PropTypes.bool.isRequired,
  onFindingUpdate: PropTypes.func.isRequired,
  patrolId: PropTypes.number.isRequired,
};

FindingList.defaultProps = {};

export default FindingList;
